import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { yoyoData } from '../pages/yoyoData';
import { resolveImageUrl } from '../utils/resolveImageUrl';
import { getProductPath } from '../utils/productSlug';
import getApiBase from '../utils/apiBase';
import { useI18n } from '../i18n/I18nContext';
import './ProductCarousel.css';

const API_BASE = getApiBase();

// Order matters: this drives carousel order.
const HERO_IDS = [1, 8, 21, 14, 6];

// Marketing taglines — keyed by product id. Production descriptions are too
// long for a card; these short lines live on the homepage only.
const HERO_TAGLINES = {
  1: 'carousel.tagline.1',
  8: 'carousel.tagline.8',
  21: 'carousel.tagline.21',
  14: 'carousel.tagline.14',
  6: 'carousel.tagline.6',
};

const HERO_BADGES = {
  1: 'carousel.badge.1',
  8: 'carousel.badge.8',
  21: 'carousel.badge.21',
  14: 'carousel.badge.14',
  6: 'carousel.badge.6',
};

const pickHeroProducts = (products) => {
  if (!Array.isArray(products) || !products.length) return [];
  const byId = new Map(products.map((p) => [p?.id, p]));
  return HERO_IDS.map((id) => byId.get(id)).filter(Boolean);
};

const ProductCarousel = () => {
  const { t, formatNumber } = useI18n();
  const [products, setProducts] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const trackRef = useRef(null);

  const formatPrice = (product) => {
    const price = Number(product?.price);
    if (!Number.isFinite(price)) return null;
    const variants = Array.isArray(product?.colors) ? product.colors : [];
    const allPrices = variants
      .map((color) => Number(color?.price))
      .filter((variantPrice) => Number.isFinite(variantPrice));
    const minPrice = Math.min(price, ...(allPrices.length ? allPrices : [price]));
    const maxPrice = Math.max(price, ...(allPrices.length ? allPrices : [price]));
    const localizedPrice = formatNumber(minPrice, { style: 'currency', currency: 'USD' });
    return minPrice === maxPrice ? localizedPrice : t('carousel.fromPrice', { price: localizedPrice });
  };

  useEffect(() => {
    let isMounted = true;
    const load = async () => {
      try {
        const params = new URLSearchParams({
          ids: HERO_IDS.join(','),
          per_page: String(HERO_IDS.length),
          include_custom: '0',
        });
        const response = await fetch(`${API_BASE}/api/products?${params.toString()}`);
        if (!response.ok) throw new Error('Failed to fetch');
        const data = await response.json();
        if (!isMounted) return;
        const responseProducts = Array.isArray(data?.items) ? data.items : data;
        if (Array.isArray(responseProducts) && responseProducts.length) {
          setAllProducts(responseProducts);
          setProducts(pickHeroProducts(responseProducts));
          return;
        }
        throw new Error('Empty response');
      } catch (error) {
        if (isMounted) {
          setAllProducts(yoyoData);
          setProducts(pickHeroProducts(yoyoData));
        }
      }
    };
    load();
    return () => {
      isMounted = false;
    };
  }, []);

  const scrollByCard = useCallback((direction) => {
    const track = trackRef.current;
    if (!track) return;
    const card = track.querySelector('.carousel-card');
    const step = card ? card.getBoundingClientRect().width + 24 : 360;
    track.scrollBy({ left: direction * step, behavior: 'smooth' });
  }, []);

  const handleKeyDown = useCallback(
    (event) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        scrollByCard(-1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        scrollByCard(1);
      } else if (event.key === 'Home') {
        event.preventDefault();
        trackRef.current?.scrollTo({ left: 0, behavior: 'smooth' });
      } else if (event.key === 'End') {
        event.preventDefault();
        const track = trackRef.current;
        if (track) track.scrollTo({ left: track.scrollWidth, behavior: 'smooth' });
      }
    },
    [scrollByCard]
  );

  if (!products.length) {
    return null;
  }

  return (
    <section className="carousel-section" aria-labelledby="carousel-title">
      <div className="carousel-header">
        <motion.div
          initial={{ y: 16, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.6 }}
        >
          <p className="carousel-eyebrow">{t('carousel.eyebrow')}</p>
          <h2 id="carousel-title" className="carousel-title">
            {t('carousel.title')}
          </h2>
        </motion.div>

        <div className="carousel-controls" role="group" aria-label={t('carousel.navigation')}>
          <button
            type="button"
            className="carousel-button"
            onClick={() => scrollByCard(-1)}
            aria-label={t('carousel.previous')}
          >
            <FaChevronLeft aria-hidden />
          </button>
          <button
            type="button"
            className="carousel-button"
            onClick={() => scrollByCard(1)}
            aria-label={t('carousel.next')}
          >
            <FaChevronRight aria-hidden />
          </button>
        </div>
      </div>

      <div
        ref={trackRef}
        className="carousel-track"
        role="region"
        aria-roledescription={t('carousel.roleDescription')}
        aria-label={t('carousel.region')}
        tabIndex={0}
        onKeyDown={handleKeyDown}
      >
        {products.map((product, idx) => {
          const path = product.path || getProductPath(product, allProducts);
          const taglineKey = HERO_TAGLINES[product.id];
          const tagline = taglineKey ? t(taglineKey) : product.description?.slice(0, 80) || '';
          const badgeKey = HERO_BADGES[product.id];
          const badge = badgeKey ? t(badgeKey) : '';
          const priceLabel = formatPrice(product);
          return (
            <motion.article
              key={product.id}
              className="carousel-card"
              initial={{ y: 20, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.5, delay: idx * 0.05 }}
            >
              <Link to={path} data-analytics-target="carousel_product" className="carousel-card-link" aria-label={t('carousel.view', { name: product.name })}>
                <div className="carousel-card-image-wrap">
                  {badge && <span className="carousel-card-badge">{badge}</span>}
                  <img
                    src={resolveImageUrl(product.imageUrl)}
                    alt={product.name}
                    className="carousel-card-image"
                    loading="lazy"
                    decoding="async"
                  />
                </div>
                <div className="carousel-card-body">
                  <h3 className="carousel-card-name">{product.name}</h3>
                  <p className="carousel-card-tagline">{tagline}</p>
                  <div className="carousel-card-footer">
                    {priceLabel && (
                      <span className="carousel-card-price">{priceLabel}</span>
                    )}
                    <span className="carousel-card-link-cta" aria-hidden>
                      {t('carousel.explore')} →
                    </span>
                  </div>
                </div>
              </Link>
            </motion.article>
          );
        })}
      </div>
    </section>
  );
};

export default ProductCarousel;
